import { useQuery } from "@tanstack/react-query";
import { payerService } from "../../services/payer.service";

export const usePayers = () => {
	return useQuery({
		queryKey: ["payers"],
		queryFn: payerService.findAll,
	});
};

export const useActivePayers = () => {
	return useQuery({
		queryKey: ["payers", "active"],
		queryFn: async () => {
			const payers = await payerService.findAll();
			return payers.filter((payer) => payer.status === "ACTIVE");
		},
	});
};

/**
 * SIMS-8647 — dependency counts for one payer, fetched only while the delete confirmation is open.
 *
 * `enabled` keeps this off the list render: the counts are needed for a single payer at the moment
 * the user asks to delete it, not for every row (decision 2026-08-31). `retry: false` so a failure
 * surfaces promptly — the dialog blocks rather than waiting through retries on a delete it cannot
 * verify.
 */
export const usePayerDependencies = (id: string | null, enabled: boolean) => {
	return useQuery({
		queryKey: ["payers", id, "dependencies"],
		queryFn: () => payerService.getDependencies(id as string),
		enabled: enabled && Boolean(id),
		retry: false,
		staleTime: 0,
	});
};

/**
 * SIMS-8622 — pattern-impact counts, fetched only when the editor asks.
 *
 * `enabled: false` always: this is triggered imperatively by `refetch()` at save time, not
 * reactively as the administrator types. Querying per keystroke would run a regex over the
 * beneficiary collection on every character.
 */
export const usePatternImpact = (id: string | null, pattern: string) => {
	return useQuery({
		queryKey: ["payers", id, "pattern-impact", pattern],
		queryFn: () => payerService.getPatternImpact(id as string, pattern),
		enabled: false,
		retry: false,
		gcTime: 0,
	});
};
